import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, BarChart3, Package, CheckCircle, XCircle, Clock, Navigation, Users, AlertTriangle, Loader2, Store, Info } from 'lucide-react';
import { formatTL } from '../../utils/money';
import TlIcon from '../../components/TlIcon';
import { getDeliveryStatus } from '../../utils/deliveryStatus';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import './ReportsPage.css';

/* Kanal tanımları TEK yerde: kart, çizelge ve tablo aynı adı ve rengi kullanır.
   Renkler panelin geri kalanıyla aynı — Hızlıyo marka yeşili, dış POS #0ea5e9. */
const CHANNELS = [
  {
    key: 'hizliyo',
    label: 'Hızlıyo restoranları',
    hint: 'Hızlıyo partneri restoranların paketleri',
    color: '#22c55e',
  },
  {
    key: 'vendor',
    label: 'Dış POS entegrasyonu',
    hint: 'Entegratör mağazalarının paketleri — ücreti restoran filoya doğrudan öder',
    color: '#0ea5e9',
  },
];

const ReportsPage = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  /** Kaynak tablosu süzgeci: 'all' · 'hizliyo' · 'vendor' */
  const [sourceChannel, setSourceChannel] = useState('all');

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  const loadReport = async () => {
    if (!startDate || !endDate) {
      setError('Lütfen başlangıç ve bitiş tarihlerini seçiniz');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Başlangıç tarihi bitiş tarihinden sonra olamaz');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setHasSearched(true);

      const response = await apiService.post(API_ENDPOINTS.FLEET_PANEL.REPORTS, {
        startDate,
        endDate
      });

      if (response.success) {
        setReportData(response.data);
      } else {
        setError(response.error || 'Rapor yüklenemedi');
        setReportData(null);
      }
    } catch (err) {
      setError('Rapor yüklenirken hata oluştu');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadReport();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0 TL';
    return formatTL(amount);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('tr-TR').format(num);
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0 dk';
    if (minutes < 60) return `${Math.round(minutes)} dk`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours} sa ${mins} dk`;
  };

  const formatDistance = (km) => {
    if (!km) return '0 km';
    return `${km.toFixed(1)} km`;
  };

  const formatDay = (value) =>
    new Date(value).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });

  const share = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

  const calculateSuccessRate = () => {
    if (!reportData || reportData.totalDeliveries === 0) return 0;
    return ((reportData.completedDeliveries / reportData.totalDeliveries) * 100).toFixed(1);
  };

  const channels = useMemo(
    () => CHANNELS.map((c) => ({ ...c, data: reportData?.[c.key] || {} })),
    [reportData]
  );

  /* Günlük akış iki kanalı AYNI ölçekte çizer: farklı ölçek küçük kanalı büyük gösterir. */
  const daily = useMemo(() => {
    const days = reportData?.hizliyo?.daily || [];
    const vendorByDate = new Map(
      (reportData?.vendor?.daily || []).map((p) => [p.date, p.totalDeliveries])
    );
    const rows = days.map((p) => ({
      date: p.date,
      hizliyo: p.totalDeliveries,
      vendor: vendorByDate.get(p.date) || 0,
    }));
    return { rows, max: Math.max(1, ...rows.map((r) => Math.max(r.hizliyo, r.vendor))) };
  }, [reportData]);

  const sources = useMemo(() => {
    if (!reportData) return [];
    return CHANNELS
      .flatMap((c) =>
        (reportData[c.key]?.sources || []).map((s) => ({ ...s, channel: c.key }))
      )
      .sort((a, b) => b.totalDeliveries - a.totalDeliveries);
  }, [reportData]);

  const visibleSources = useMemo(
    () => (sourceChannel === 'all' ? sources : sources.filter((s) => s.channel === sourceChannel)),
    [sources, sourceChannel]
  );

  return (
    <div className="fr-reports-page">
      <div className="page-header">
        <div className="header-left">
          <div className="page-icon"><BarChart3 size={20} /></div>
          <div className="page-title">
            <h1>Raporlar</h1>
            <p>Filo performans raporlarınızı görüntüleyin</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSearch} className="fr-filter-section">
        <div className="fr-filter-content">
          <div className="fr-date-inputs">
            <div className="fr-date-group">
              <label>
                <Calendar size={16} />
                Başlangıç Tarihi
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="fr-date-group">
              <label>
                <Calendar size={16} />
                Bitiş Tarihi
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            <BarChart3 size={18} />
            {loading ? 'Yükleniyor...' : 'Rapor Oluştur'}
          </button>
        </div>
      </form>

      {error && (
        <div className="fr-alert fr-alert-error">
          {error}
        </div>
      )}

      {loading && (
        <div className="page-loading">
          <Loader2 className="spinning" size={40} />
          <p>Rapor hazırlanıyor...</p>
        </div>
      )}

      {!loading && hasSearched && !reportData && !error && (
        <div className="page-empty">
          <BarChart3 size={64} />
          <h3>Rapor Bulunamadı</h3>
          <p>Seçilen tarih aralığında veri bulunamadı</p>
        </div>
      )}

      {!loading && reportData && (
        <div className="fr-reports-content">
          <div className="fr-stats-grid">
            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-primary">
                <Package size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Toplam Teslimat</span>
                <span className="fr-stat-value">{formatNumber(reportData.totalDeliveries)}</span>
                <span className="fr-stat-subtitle">iki kanal birlikte</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-success">
                <CheckCircle size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Tamamlanan</span>
                <span className="fr-stat-value">{formatNumber(reportData.completedDeliveries)}</span>
                <span className="fr-stat-subtitle">{calculateSuccessRate()}% başarı</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-danger">
                <XCircle size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Başarısız</span>
                <span className="fr-stat-value">{formatNumber(reportData.failedDeliveries)}</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-warning">
                <AlertTriangle size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">İptal Edilen</span>
                <span className="fr-stat-value">{formatNumber(reportData.cancelledDeliveries)}</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-money">
                <TlIcon size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Toplam Gelir</span>
                <span className="fr-stat-value">{formatCurrency(reportData.totalRevenue)}</span>
                <span className="fr-stat-subtitle">yalnız Hızlıyo kanalı</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-info">
                <Clock size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Ort. Teslimat Süresi</span>
                <span className="fr-stat-value">{formatTime(reportData.averageDeliveryTimeMinutes)}</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-secondary">
                <Navigation size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Ort. Mesafe</span>
                <span className="fr-stat-value">{formatDistance(reportData.averageDistanceKm)}</span>
              </div>
            </div>

            <div className="fr-stat-card">
              <div className="fr-stat-icon fr-icon-primary">
                <Users size={20} />
              </div>
              <div className="fr-stat-content">
                <span className="fr-stat-label">Çalışan Kurye</span>
                <span className="fr-stat-value">{formatNumber(reportData.uniqueCouriersWorked)}</span>
              </div>
            </div>
          </div>

          {/* Kanal kırılımı. İki kanal BİLEREK toplanmıyor: aynı sütuna koymak
              hangi tarafın büyüdüğünü gizler. Üstteki adetler ikisinin toplamıdır. */}
          <div className="fr-channels">
            <div className="fr-channel-head">
              <h3>Kanal Kırılımı</h3>
              <span>Üstteki adetler bu iki kanalın toplamıdır</span>
            </div>

            <div className="fr-channel-grid">
              {channels.map((c) => (
                <div key={c.key} className="fr-channel-card" style={{ '--fr-channel': c.color }}>
                  <div className="fr-channel-title">
                    <strong>{c.label}</strong>
                    <span>{c.hint}</span>
                  </div>

                  <div className="fr-channel-lead">
                    <b>{formatNumber(c.data.totalDeliveries ?? 0)}</b>
                    <span>paket, toplamın %{share(c.data.totalDeliveries ?? 0, reportData.totalDeliveries)}'i</span>
                  </div>
                  <div className="fr-channel-meter">
                    <i style={{ width: `${share(c.data.totalDeliveries ?? 0, reportData.totalDeliveries)}%` }} />
                  </div>

                  <div className="fr-channel-rows">
                    <div><span>Tamamlanan</span><b>{formatNumber(c.data.completedDeliveries ?? 0)}</b></div>
                    <div><span>Başarısız</span><b>{formatNumber(c.data.failedDeliveries ?? 0)}</b></div>
                    <div><span>İptal</span><b>{formatNumber(c.data.cancelledDeliveries ?? 0)}</b></div>
                    <div><span>Ort. süre</span><b>{formatTime(c.data.averageDeliveryTimeMinutes ?? 0)}</b></div>
                    <div><span>Ort. mesafe</span><b>{formatDistance(c.data.averageDistanceKm ?? 0)}</b></div>
                    <div><span>Çalışan kurye</span><b>{formatNumber(c.data.uniqueCouriersWorked ?? 0)}</b></div>
                  </div>

                  {/* Tutar alanı boşsa rakam bizde kayıtlı değil demektir; sıfır yazmak
                      "hiç kazanmadı" anlamına gelirdi. */}
                  {c.data.revenue == null ? (
                    <p className="fr-channel-note">
                      <Info size={14} />
                      Paket bedeli sistemimizde tutulmaz: restoran ücreti filoya doğrudan öder.
                      Bu kanalda yalnız paket adedi raporlanır.
                    </p>
                  ) : (
                    <div className="fr-channel-money">
                      <span>Gelir</span>
                      <b>{formatCurrency(c.data.revenue)}</b>
                    </div>
                  )}

                  <div className="fr-channel-status">
                    <h4>Durum dağılımı</h4>
                    {(c.data.statusBreakdown || []).length === 0 ? (
                      <p className="fr-muted">Bu aralıkta paket yok.</p>
                    ) : (
                      <ul>
                        {c.data.statusBreakdown.map((s) => (
                          <li key={s.statusCode}>
                            <span className="fr-status-name">{getDeliveryStatus(s.statusCode).label}</span>
                            <span className="fr-status-bar">
                              <i style={{ width: `${share(s.count, c.data.totalDeliveries ?? 0)}%` }} />
                            </span>
                            <b>{formatNumber(s.count)}</b>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="fr-section">
            <div className="fr-section-header">
              <BarChart3 size={18} />
              <h2>Günlük Paket Akışı</h2>
            </div>
            <div className="fr-legend">
              {CHANNELS.map((c) => (
                <span key={c.key}><i style={{ background: c.color }} />{c.label}</span>
              ))}
            </div>

            {daily.rows.length === 0 ? (
              <p className="fr-muted">Seçilen aralıkta gün verisi yok.</p>
            ) : (
              <div className="fr-daily-scroll">
                <div className="fr-daily">
                  {daily.rows.map((d) => (
                    <div
                      className="fr-day"
                      key={d.date}
                      title={`${formatDay(d.date)}, Hızlıyo ${d.hizliyo}, Dış POS ${d.vendor}`}
                    >
                      <div className="fr-day-bars">
                        <i className="fr-bar-hizliyo" style={{ height: `${(d.hizliyo / daily.max) * 100}%` }} />
                        <i className="fr-bar-vendor" style={{ height: `${(d.vendor / daily.max) * 100}%` }} />
                      </div>
                      <span className="fr-day-label">{formatDay(d.date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="fr-section">
            <div className="fr-section-header">
              <Store size={18} />
              <h2>Paket Kaynakları</h2>
            </div>

            <div className="fr-source-filters">
              {[
                { key: 'all', label: 'Tümü' },
                ...CHANNELS.map((c) => ({ key: c.key, label: c.label })),
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`fr-chip${sourceChannel === f.key ? ' is-active' : ''}`}
                  onClick={() => setSourceChannel(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {visibleSources.length === 0 ? (
              <div className="fr-empty-table">
                <Store size={48} />
                <p>Bu aralıkta bu kanaldan paket gelmedi.</p>
              </div>
            ) : (
              <div className="fr-couriers-table-container">
                <table className="fr-couriers-table">
                  <thead>
                    <tr>
                      <th>Restoran / Mağaza</th>
                      <th>Kanal</th>
                      <th>Paket</th>
                      <th>Tamamlanan</th>
                      <th>Başarı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSources.map((s) => {
                      const channel = CHANNELS.find((c) => c.key === s.channel);
                      const rate = share(s.completedDeliveries, s.totalDeliveries);
                      return (
                        <tr key={`${s.channel}-${s.name}`}>
                          <td>{s.name}</td>
                          <td>
                            <span className="fr-channel-chip" style={{ '--fr-channel': channel.color }}>
                              {channel.label}
                            </span>
                          </td>
                          <td>{formatNumber(s.totalDeliveries)}</td>
                          <td>{formatNumber(s.completedDeliveries)}</td>
                          <td>
                            <div className="fr-progress-container">
                              <div className="fr-progress-bar">
                                <div className="fr-progress-fill" style={{ width: `${rate}%` }} />
                              </div>
                              <span className="fr-progress-text">%{rate}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
